import type React from "react";
import { useContext, useEffect, useState } from "react";

import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetCareEventsSuggested } from "api/hooks/useGetCareEventsSuggested";
import { getPatientCareEventsQueryKey, useGetPatientCareEventsQuery } from "api/hooks/useGetPatientCareEventsQuery";
import { getPTCareEventsQueryKey } from "api/hooks/useGetPTCareEventsQuery";
import type { CareEvent } from "api/models/CareEvent";
import { useProfileContext } from "contexts/ProfileContext";
import { Pagination } from "shared/atoms/Pagination";
import { Container, Notifications, TableHeader } from "shared/molecules/MUIDataGrid/MUIDataGrid";
import { CurrentPatientContext } from "utils/contexts";

import { MedicalNote } from "./components/MedicalNote";
import { StatusContainer } from "./components/StatusContainer";

const LIMIT = 5;

export const SignNotesTable: React.FC = () => {
  const { updateIsCreatable, activeCareEventNote } = useContext(CurrentPatientContext);
  const patientId = Number(useParams<Record<string, string>>().patientId);
  const {
    data: careEvents = [],
    isError,
    isLoading,
    isSuccess,
  } = useGetPatientCareEventsQuery({ patientId, signed: false });
  const { data: careEventSuggested } = useGetCareEventsSuggested([patientId]);
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  invariant(profile, "Profile context not set");
  const queryClient = useQueryClient();

  useEffect(() => {
    updateIsCreatable();
  }, [patientId]);

  const mappedCareEventSuggested = careEventSuggested?.map(ce => ({ ...ce, suggested: true }));
  const mergedCareEvents = careEvents.concat((mappedCareEventSuggested ?? []) as CareEvent[]);

  const activeCareEvent = mergedCareEvents.find(ce => ce.id === activeCareEventNote);
  const totalLength = mergedCareEvents.length;
  const index = Math.min(totalLength - 1, Math.max(0, mergedCareEvents.indexOf(activeCareEvent as CareEvent)));

  useEffect(() => {
    const initialOffset = index - (index % LIMIT);
    setOffset(initialOffset);
  }, [index]);

  const [offset, setOffset] = useState(0);

  const careEventSubset = mergedCareEvents.slice(offset, offset + LIMIT);
  const hasNextPage = totalLength > offset + LIMIT;
  const hasPreviousPage = !!offset;

  // Cleanup. Invalidation to guarantee server state sync
  useEffect(
    () => () => {
      queryClient.invalidateQueries(getPatientCareEventsQueryKey({ patientId, signed: false }));
      queryClient.invalidateQueries(getPTCareEventsQueryKey({ userId: profile.id }));
    },
    [queryClient, patientId, profile]
  );

  return (
    <Container>
      <TableHeader>
        <HeaderTitle>
          <Typography variant="h3">
            {totalLength > 0 ? t("dashboard.tables.notes.title") : t("dashboard.tables.notes.no_notes")}
          </Typography>
          {totalLength > 0 && <Notifications>{totalLength}</Notifications>}
        </HeaderTitle>
      </TableHeader>
      <StatusContainer isError={isError} isLoading={isLoading} />
      {isSuccess && (
        <NotesList>
          {careEventSubset.map(careEvent => (
            <MedicalNote {...careEvent} key={careEvent.id} />
          ))}
        </NotesList>
      )}
      {totalLength > LIMIT && (
        <PaginationContainer>
          <Pagination
            totalCount={totalLength}
            first={offset + 1}
            last={Math.min(offset + LIMIT, totalLength)}
            pageInfo={{ hasNextPage, hasPreviousPage }}
            onPageChange={goTo => (goTo === "next" ? setOffset(offset + LIMIT) : setOffset(offset - LIMIT))}
          />
        </PaginationContainer>
      )}
    </Container>
  );
};

const HeaderTitle = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 60px;
  border-radius: 8px;
  padding: 0 ${props => props.theme.spacing.S_20};
  background: ${props => props.theme.colors.redesign.db1};
`;
